package br.com.heyagenda.data.repository

import br.com.heyagenda.data.remote.FirebaseAuthDataSource
import br.com.heyagenda.data.remote.FirestoreAppointmentDataSource
import br.com.heyagenda.data.remote.FirestoreClientDataSource
import br.com.heyagenda.domain.model.SavedClient
import br.com.heyagenda.domain.repository.ClientRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

class ClientRepositoryImpl(
    private val clientDataSource: FirestoreClientDataSource,
    private val authDataSource: FirebaseAuthDataSource,
    private val appointmentDataSource: FirestoreAppointmentDataSource? = null
) : ClientRepository {

    private val localClients = MutableStateFlow<List<SavedClient>>(emptyList())

    override fun getSavedClients(): Flow<List<SavedClient>> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid == null) {
            return localClients.asStateFlow()
        }
        return clientDataSource.observeClients(uid)
    }

    override suspend fun saveClient(client: SavedClient): Result<Unit> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            val res = clientDataSource.saveClient(uid, client)
            if (res.isSuccess) return res
        }

        localClients.update { list ->
            val cleanName = client.name.trim().lowercase()
            val existingIndex = list.indexOfFirst { it.name.trim().lowercase() == cleanName }
            if (existingIndex >= 0) {
                val old = list[existingIndex]
                val updated = old.copy(
                    totalAppointments = old.totalAppointments + 1,
                    phone = client.phone ?: old.phone,
                    lastAppointmentDate = client.lastAppointmentDate ?: old.lastAppointmentDate,
                    lastServiceName = client.lastServiceName ?: old.lastServiceName
                )
                list.toMutableList().apply { set(existingIndex, updated) }
            } else {
                list + client
            }
        }
        return Result.success(Unit)
    }

    override suspend fun deleteClient(clientId: String): Result<Unit> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            val res = clientDataSource.deleteClient(uid, clientId)
            if (res.isSuccess) return res
        }
        localClients.update { list ->
            list.filterNot { it.id == clientId }
        }
        return Result.success(Unit)
    }

    override suspend fun syncClientsFromAppointments(): Result<Unit> {
        val uid = authDataSource.currentFirebaseUser?.uid ?: run {
            println(">>> [ClientRepo SYNC] currentUser is NULL!")
            return Result.success(Unit)
        }
        val apptDs = appointmentDataSource ?: run {
            println(">>> [ClientRepo SYNC] appointmentDataSource is NULL!")
            return Result.success(Unit)
        }
        return try {
            val existingClients = clientDataSource.getClients(uid)

            val appointments = apptDs.getAppointments(uid)
            if (appointments.isEmpty()) return Result.success(Unit)

            val existingPhones = existingClients.mapNotNull { it.phone?.filter { ch -> ch.isDigit() }?.let { d ->
                if (d.length > 11 && d.startsWith("55")) d.drop(2) else d
            }?.take(11) }.filter { it.isNotBlank() }.toSet()
            val existingNames = existingClients.map { it.name.trim().lowercase() }.filter { it.isNotBlank() }.toSet()

            // Group appointments by client phone or client name
            val grouped = appointments
                .filter { it.clientName.isNotBlank() }
                .groupBy { appt ->
                    val rawPhone = appt.clientPhone.orEmpty()
                    val digits = rawPhone.filter { it.isDigit() }.let {
                        if (it.length > 11 && it.startsWith("55")) it.drop(2) else it
                    }.take(11)
                    if (digits.isNotBlank()) "phone:$digits" else "name:${appt.clientName.trim().lowercase()}"
                }

            for ((key, appts) in grouped) {
                val isPhone = key.startsWith("phone:")
                val keyVal = key.substringAfter(":")
                val alreadyExists = if (isPhone) existingPhones.contains(keyVal) else existingNames.contains(keyVal)

                if (!alreadyExists) {
                    val latest = appts.maxByOrNull { "${it.date} ${it.startTime}" } ?: appts.first()
                    val clientName = latest.clientName.trim()
                    val clientPhone = latest.clientPhone?.takeIf { it.isNotBlank() }

                    clientDataSource.saveClient(
                        uid,
                        SavedClient(
                            id = "",
                            name = clientName,
                            phone = clientPhone,
                            totalAppointments = appts.size,
                            lastAppointmentDate = latest.date.takeIf { it.isNotBlank() },
                            lastServiceName = latest.serviceName.takeIf { it.isNotBlank() },
                            notes = null
                        )
                    )
                }
            }
            Result.success(Unit)
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }
}
